<template>
  <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-12 last-child:pr-3 text-sm">
    <td class="py-3 text-left">
      <div class="flex items-center space-x-2">
        <button
          type="button"
          class="btn-icon btn-icon-default"
          data-test="expand-dayworks-records"
          @click.stop="toggleRecords"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 transition-all duration-200"
            :class="[{'rotate-90': expanded}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <span
          v-if="type === DayworksType.plant"
          class="max-w-20 font-semibold text-gray-900"
        >{{ code }}</span>

        <p class="w-60 text-gray-700">
          {{ description }}
        </p>
      </div>
    </td>

    <td class="pr-3">
      {{ australianCurrency(calculatedAmount) }}
    </td>

    <td v-if="claim.boq_type === 'subcontractor_schedule_of_rates'" />

    <td
      v-if="!falsey(dayworksCost?.current_claim) && !typingClaim && canEditClaim"
      class="bg-green-200/50 cursor-pointer"
      :class="{ 'text-red-600': claimExceedsCalculated }"
      @click="typeClaim"
    >
      <div class="group flex items-center justify-end">
        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-red inline-block invisible"
          @click.stop.capture="removeClaim"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>
        {{ australianCurrency(dayworksCost.current_claim) }}
      </div>
    </td>

    <td
      v-else-if="canEditClaim"
      class="bg-green-200/50"
      :class="{ 'text-red-600': claimExceedsCalculated }"
    >
      <div class="flex items-center justify-end space-x-1">
        <AppCurrencyInput
          ref="claimInput"
          v-model.number="costForm.form.current_claim"
          name="current_claim"
          type="number"
          class="w-20 h-6 ml-auto form-input"
          :class="{ 'text-red-600': claimExceedsCalculated }"
          @blur="stopTypingClaim"
          @keyup.enter="stopTypingClaim"
          @keyup.esc="typingClaim = false"
        />

        <button
          v-if="showPrefillTick"
          type="button"
          class="btn-icon btn-icon-green"
          title="Prefill claim amount"
          data-test="prefill-dayworks-claim"
          @click.stop="prefillClaim"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
        </button>
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
      :class="{ 'text-red-600': claimExceedsCalculated }"
    >
      {{ !falsey(dayworksCost?.current_claim) ? australianCurrency(dayworksCost.current_claim) : '' }}
    </td>

    <td class="bg-green-200/50" />

    <td class="bg-green-200/50" />

    <td class="bg-green-200/50">
      {{ australianCurrency(previousAmount) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(previousAmount + (dayworksCost?.current_claim || 0)) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ calculatedAmount > 0 && dayworksCost?.current_claim
          ? `${australianNumber((dayworksCost.current_claim / calculatedAmount) * 100)}%`
          : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    />

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ calculatedAmount > 0 ? australianNumber((previousAmount / calculatedAmount) * 100) + '%' : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ calculatedAmount > 0
        ? australianNumber(((previousAmount + (dayworksCost?.current_claim || 0)) / calculatedAmount) * 100) + '%'
        : '' }}
    </td>

    <td class="pr-3">
      {{ calculatedAmount
        ? australianCurrency(calculatedAmount - previousAmount - (dayworksCost?.current_claim || 0))
        : '' }}
    </td>
  </tr>

  <tr
    v-if="expanded"
    class="bg-white"
  >
    <td
      colspan="100%"
      class="px-6 py-3"
    >
      <div
        v-if="loadingRecords"
        class="flex items-center justify-center space-x-2 text-sm text-gray-500 py-2 w-full"
      >
        <AppSpinner />
      </div>

      <template v-else>
        <div
          v-if="records.length"
          class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-y-auto max-h-[320px]"
        >
          <table class="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr class="child:border-b child:border-gray-200 bg-gray-50 sticky top-0 child:py-2.5 child:px-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                <th>Date</th>

                <th class="text-right">
                  Hours
                </th>

                <th class="text-right">
                  Rate
                </th>

                <th class="text-right">
                  Cost
                </th>

                <th>WBS</th>

                <th>Area</th>

                <th>Element</th>

                <th>Activity</th>

                <th>Approved By</th>

                <th>Approved At</th>

                <th class="text-right">
                  Paid
                </th>

                <th>Status</th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-100">
              <tr
                v-for="record in records"
                :key="record.id"
                class="child:p-2"
                :class="{ 'bg-amber-50': !record.$claimable && record.$status !== 'Claimed' }"
              >
                <td>{{ record.day }}</td>

                <td class="text-right">
                  {{ record.hours }}
                </td>

                <td class="text-right">
                  {{ australianCurrency(record.accrual_rate) }}
                </td>

                <td class="text-right">
                  {{ record.$cost != null ? australianCurrency(record.$cost) : '' }}
                </td>

                <td>{{ record.wbsCode?.code }}</td>

                <td>{{ record.area }}</td>

                <td>{{ record.element }}</td>

                <td>{{ record.activity }}</td>

                <td>{{ record.user?.name }}</td>

                <td>{{ record.approvedAt?.toLocaleString() }}</td>

                <td class="text-right">
                  {{ record.paid != null ? australianCurrency(record.paid) : '' }}
                </td>

                <td>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="statusBadgeClass(record.$status)"
                  >
                    {{ record.$status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="bg-white rounded-lg border border-gray-200 p-4 text-center text-sm text-gray-500"
        >
          No records for this period.
        </div>

        <div
          v-if="hasMorePages"
          class="mt-2"
        >
          <button
            type="button"
            class="btn btn--secondary-blue text-xs"
            :disabled="loadingMore"
            @click="loadMore"
          >
            {{ loadingMore ? 'Loading...' : 'Load more' }}
          </button>
        </div>
      </template>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, parseUrlParams } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import { DayworksType } from '@/models/DayworksCost';
import Labour from '@/models/Labour';
import Plant from '@/models/Plant';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { useLabourDailyRecordStore } from '@/store/labourDailyRecord';
import { usePlantDailyRecordsStore } from '@/store/plantDailyRecord';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  type: ObjectValues<typeof DayworksType>;
  labour?: Labour;
  plant?: Plant;
  claim: Claim;
  showPercentages?: boolean;
}>();

const signedParams = useSignedData();
const dayworksCostsStore = useDayworksCostsStore();
const labourDailyRecordsStore = useLabourDailyRecordStore();
const plantDailyRecordsStore = usePlantDailyRecordsStore();
const item = computed(() => props.type === DayworksType.labour ? props.labour : props.plant);
const itemId = computed(() => item.value?.id);

const dayworksCost = computed(() => {
  if(!itemId.value) {
    return undefined;
  }

  if(props.type === DayworksType.labour) {
    return dayworksCostsStore.models
      .where('claim_id', props.claim.id)
      .where('labour_id', itemId.value)
      .first();
  }

  return dayworksCostsStore.models
    .where('claim_id', props.claim.id)
    .where('plant_id', itemId.value)
    .first();
});

const description = computed(() => {
  if(props.type === DayworksType.labour) {
    return `${props.labour?.first_name ?? ''} ${props.labour?.last_name ?? ''}`.trim();
  }

  return props.plant?.description ?? '';
});

const code = computed(() => {
  if(props.type === DayworksType.labour) {
    return 'Labour';
  }

  return props.plant?.fleet ?? 'Plant';
});

const calculatedAmount = computed(() => {
  if(item.value?.$claim_id === props.claim.id) {
    return item.value.calculatedAmount;
  }

  return 0;
});

const previousAmount = computed(() => {
  if(item.value?.$claim_id === props.claim.id) {
    return item.value.$previous || 0;
  }

  return dayworksCost.value?.$previous || 0;
});

const canEditClaim = computed(() => {
  return props.claim.status === ClaimStatus.draft_subcontractor;
});

const typingClaim = ref(falsey(dayworksCost.value?.current_claim));
const claimInput = useTemplateRef('claimInput');

const costForm = useFormData({
  id: undefined as number | undefined,
  current_claim: undefined as number | undefined,
  type: props.type,
  labour_id: props.labour?.id,
  plant_id: props.plant?.id,
});

watch(() => dayworksCost.value, (newValue, oldValue) => {
  if(newValue && oldValue && newValue.id === oldValue.id) {
    return;
  }

  costForm.form.current_claim = newValue?.current_claim;

  if(falsey(newValue?.current_claim)) {
    costForm.form.current_claim = calculatedAmount.value || undefined;
  }

  typingClaim.value = falsey(newValue?.current_claim);
}, { immediate: true });

const claimExceedsCalculated = computed(() => {
  const amount = typingClaim.value ?
    costForm.form.current_claim :
    dayworksCost.value?.current_claim;

  if(falsey(amount)) {
    return false;
  }

  return (amount as number) - calculatedAmount.value > 0.01;
});

const showPrefillTick = computed(() => {
  return canEditClaim.value &&
    typingClaim.value &&
    falsey(dayworksCost.value?.current_claim) &&
    calculatedAmount.value !== 0;
});

const saveClaimAction = useStoreApiAction(dayworksCostsStore.saveSubcontractorClaim);
const deleteSubcontractorClaimAction = useStoreApiAction(dayworksCostsStore.deleteSubcontractorClaim);
const fetchLabourRecordsAction = useStoreApiAction(labourDailyRecordsStore.fetchSignedRecords);
const fetchPlantRecordsAction = useStoreApiAction(plantDailyRecordsStore.fetchSignedRecords);

const typeClaim = () => {
  if(canEditClaim.value) {
    typingClaim.value = true;
    costForm.form.current_claim = dayworksCost.value?.current_claim;
    nextTick(() => claimInput.value?.focusInput());
  }
};

const prefillClaim = async () => {
  costForm.form.current_claim = calculatedAmount.value;
  await stopTypingClaim();
};

const removeClaim = () => {
  if(!dayworksCost.value) {
    return;
  }

  deleteSubcontractorClaimAction.request(
    props.claim.id,
    parseUrlParams(signedParams.meta.dayworks_destroy_url as string),
    {
      id: dayworksCost.value.id,
    },
  )
    .then(() => {
      costForm.form.id = undefined;
      costForm.form.current_claim = undefined;
      typingClaim.value = true;
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid amount',
          text: error.data?.message,
        });
      }

      if(dayworksCost.value) {
        costForm.form.current_claim = dayworksCost.value.current_claim;
      }
    });
};

const stopTypingClaim = async () => {
  if(
    (falsey(costForm.form.current_claim) && !dayworksCost.value) ||
    !typingClaim.value
  ) {
    return;
  }

  if((costForm.form.current_claim as number) - calculatedAmount.value > 0.01) {
    await Swal.fire({
      icon: 'warning',
      title: 'Amount exceeds calculated.',
      text: `The amount cannot be greater than the calculated dayworks total of ${
        australianCurrency(calculatedAmount.value)
      }.`,
    });

    costForm.form.current_claim = dayworksCost.value?.current_claim;
    typingClaim.value = true;

    return;
  }

  if(dayworksCost.value && falsey(costForm.form.current_claim)) {
    deleteSubcontractorClaimAction.request(
      props.claim.id,
      parseUrlParams(signedParams.meta.dayworks_destroy_url as string),
      {
        id: dayworksCost.value.id,
      },
    ).then(() => {
      costForm.form.id = undefined;
      costForm.form.current_claim = undefined;
      typingClaim.value = true;
    }).catch(() => {
      typingClaim.value = false;
    });
  } else {
    saveClaimAction.request(
      props.claim.id,
      parseUrlParams(signedParams.meta.dayworks_claim_url as string),
      costForm.form,
    ).then(() => {
      typingClaim.value = false;
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid data',
          text: error.data?.message,
        });
      }

      if(dayworksCost.value) {
        costForm.form.current_claim = dayworksCost.value.current_claim;
      }

      typingClaim.value = false;
    });
  }
};

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'Claimed':

    case 'Claimed on later claim':
      return 'bg-blue-100 text-blue-800';

    case 'Claimable':
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-amber-100 text-amber-800';
  }
};

const expanded = ref(false);
const loadingRecords = ref(false);
const loadingMore = ref(false);
const recordIds = ref<number[]>([]);
const currentPage = ref(1);
const lastPage = ref(1);
const hasMorePages = computed(() => currentPage.value < lastPage.value);

const records = computed(() => {
  if(props.type === DayworksType.labour) {
    return labourDailyRecordsStore.models.query().find(recordIds.value);
  }

  return plantDailyRecordsStore.models.query().find(recordIds.value);
});

const labourRecordsSignedParams = computed(() =>
  parseUrlParams(signedParams.meta.dayworks_labour_records_url as string)
);

const plantRecordsSignedParams = computed(() => parseUrlParams(signedParams.meta.dayworks_plant_records_url as string));

const fetchRecords = (page: number) => {
  if(props.type === DayworksType.labour) {
    return fetchLabourRecordsAction.request(
      props.claim.id,
      labourRecordsSignedParams.value,
      props.labour!.id,
      page,
    );
  }

  return fetchPlantRecordsAction.request(
    props.claim.id,
    plantRecordsSignedParams.value,
    props.plant!.id,
    page,
  );
};

const toggleRecords = async () => {
  expanded.value = !expanded.value;

  if(!expanded.value) {
    return;
  }

  loadingRecords.value = true;

  try {
    const data = await fetchRecords(1);

    recordIds.value = data.data.map((record) => record.id);
    currentPage.value = data.meta.current_page;
    lastPage.value = data.meta.last_page;
  } catch (_error) {
    await Swal.fire({
      icon: 'error',
      title: 'Failed to load dayworks records',
    });

    expanded.value = false;
  } finally {
    loadingRecords.value = false;
  }
};

const loadMore = async () => {
  if(!hasMorePages.value) {
    return;
  }

  loadingMore.value = true;

  try {
    const data = await fetchRecords(currentPage.value + 1);
    const moreIds = data.data.map((record) => record.id);

    recordIds.value = recordIds.value.concat(moreIds);
    currentPage.value = data.meta.current_page;
    lastPage.value = data.meta.last_page;
  } catch (_error) {
    await Swal.fire({
      icon: 'error',
      title: 'Failed to load more records',
    });
  } finally {
    loadingMore.value = false;
  }
};
</script>
